// /src/pages/InsuranceAnalysisPage/useInsuranceAnalysisData.js
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import useAxios from '../../hooks/useAxios';
import { urls } from '../../components/config/variables';
import AuthContext from '../../contexts/auth-context';
import AppContext from '../../contexts/app-context';
import { validateStatistics, validateStatisticsKey } from '../../Utils/validateHttpResponse';

const ONLY_ONCE = false; // set true if you want to fetch strictly once per mount (ignores date filter changes)

const useInsuranceAnalysisData = (type) => {
  const { operation } = useAxios();
  // Keep the latest operation in a ref so effect doesn't retrigger on identity change
  const operationRef = useRef(operation);
  useEffect(() => {
    operationRef.current = operation;

    // eslint-disable-next-line
  }, [operation]);

  const authCtx = useContext(AuthContext);
  const token = authCtx?.token || '';
  const appCtx = useContext(AppContext);
  const userRole = appCtx.userRole
  const globalFilterQuery = appCtx?.globalFilterQuery || '';

  const [statsData, setStatsData] = useState({
    total_expenditure: { value: 0, change: undefined, increase: true, percentage: undefined },
    total_encounters: { value: 0, change: undefined, increase: false, percentage: undefined },
    avg_cost: { value: 0, change: undefined, increase: true, percentage: undefined },
    total_patients: { value: 0, change: undefined, increase: true, percentage: undefined },
    active_doctors: { value: 0, change: undefined, increase: true, percentage: undefined },
    top_drug: { value: 0 },
    money_saved: { value: 0, change: undefined, increase: true, percentage: undefined },
    rejected_icds: { value: 0, change: undefined, increase: true, percentage: undefined },
    rejected_cpts: { value: 0, change: undefined, increase: true, percentage: undefined },
    rejected_drugs: { value: 0, change: undefined, increase: true, percentage: undefined },
    approved_ratio: { value: 0, change: undefined, increase: true, percentage: undefined },
    total_patient_not_refile_yet: { value: 0, change: undefined, increase: true, percentage: undefined },
    total_cost_on_upcoming_refiles: { value: 0, change: undefined, increase: true, percentage: undefined },
    total_patient_who_refile_this_month: { value: 0, change: undefined, increase: true, percentage: undefined },
    expensive_drugs_prescribed: { value: 0, change: undefined, increase: true, percentage: undefined },
    patient_abusing_system: { value: 0, change: undefined, increase: true, percentage: undefined },
    high_rejection_providers: { value: 0, change: undefined, increase: true, percentage: undefined },
    excessive_encounters: { value: 0, change: undefined, increase: true, percentage: undefined },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track/abort in-flight requests and ignore stale responses
  const abortRef = useRef(null);
  const latestReqIdRef = useRef(0);
  const fetchedOnceRef = useRef(false);

  // Change this key when you actually want a refetch
  const queryKey = useMemo(() => {
    if (!token) return '';
    if (ONLY_ONCE) return `${urls.BASIC_STATS}::${token}::once`;
    return `${urls.BASIC_STATS}${globalFilterQuery}::${token}`;
  }, [token, globalFilterQuery]);

  useEffect(() => {
    if (!queryKey) return;

    // If you want strictly once per mount
    if (ONLY_ONCE && fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;

    // Cancel prior request if still running
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const reqId = ++latestReqIdRef.current;
    setLoading(true);

    const convertFormat = (data) => {
      const result = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          result[key] = { value: data[key] };
        }
      }
      return result;
    }

    const url = {
      'facility_admin': urls.FACILITY_BASIC_STATS,
      'insurance_admin': urls.INSURANCE_BASIC_STATS
    }

    const request = () =>
      new Promise((resolve, reject) => {
        const params = new URLSearchParams(globalFilterQuery);
        const foundType  = params.get('type');
        if(!foundType) {
          params.set("type", type);
        }
        operationRef.current(
          {
            method: 'GET',
            url: `${url[userRole]}?${params}`,
            headers: { Authorization: `token ${token}` },
            signal: controller.signal, // make sure your useAxios forwards this to axios/fetch
          },
          (data) => resolve(data),
          (err) => reject(err)
        );
      });

    (async () => {
      try {
        const response = await request();
        let data

        if (type === 'weekly') {
          if (validateStatistics(response)) {
            data = response
          } else {
            console.log('Invalid Statistics response')
          }
        } else {
          if (validateStatisticsKey(response)) {
            data = convertFormat(response)
          } else {
            console.log('Invalid Statistics key response')
          }
        }

        if (controller.signal.aborted || reqId !== latestReqIdRef.current) return;
  
          const transformedData = {
            total_expenditure: {
              value: data.approved_expenses?.value,
              change: (data.approved_expenses?.change ?? 0).toFixed(2),
              increase: data.approved_expenses?.status === '+',
              percentage: data.approved_expenses?.percentage != null
                ? `${data.approved_expenses.status === '-' ? '' : data.approved_expenses.status}${data.approved_expenses.percentage.toFixed(2)}`
                : null,
            },
            total_encounters: {
              value: data.total_encounters?.value,
              change: (data.total_encounters?.change ?? 0).toFixed(2),
              increase: data.total_encounters?.status === '+',
              percentage: data.total_encounters?.percentage != null
                ? `${data.total_encounters.status === '-' ? '' : data.total_encounters.status}${data.total_encounters.percentage.toFixed(2)}`
                : null,
            },
            avg_cost: {
              value: data.avg_encounter_cost?.value,
              change: (data.avg_encounter_cost?.change ?? 0).toFixed(2),
              increase: data.avg_encounter_cost?.status === '+',
              percentage: data.avg_encounter_cost?.percentage != null
                ? `${data.avg_encounter_cost.status === '-' ? '' : data.avg_encounter_cost.status}${data.avg_encounter_cost.percentage.toFixed(2)}`
                : null,
            },
            total_patients: {
              value: data.total_patients?.value,
              change: (data.total_patients?.change ?? 0).toFixed(2),
              increase: data.total_patients?.status === '+',
              percentage: data.total_patients?.percentage != null
                ? `${data.total_patients.status === '-' ? '' : data.total_patients.status}${data.total_patients.percentage.toFixed(2)}`
                : null,
            },
            active_doctors: {
              value: data.total_providers?.value,
              change: (data.total_providers?.change ?? 0).toFixed(2),
              increase: data.total_providers?.status === '+',
              percentage: data.total_providers?.percentage != null
                ? `${data.total_providers.status === '-' ? '' : data.total_providers.status}${data.total_providers.percentage.toFixed(2)}`
                : null,
            },
            top_drug: {
              value: data.most_used_drug?.value,
            },
            money_saved: {
              value: data.amount_saved?.value,
              change: (data.amount_saved?.change ?? 0).toFixed(2),
              increase: data.amount_saved?.status === '+',
              percentage: data.amount_saved?.percentage != null
                ? `${data.amount_saved.status === '-' ? '' : data.amount_saved.status}${data.amount_saved.percentage.toFixed(2)}`
                : null,
            },
            rejected_icds: {
              value: data.total_rejected_icds?.value,
              change: (data.total_rejected_icds?.change ?? 0).toFixed(2),
              increase: data.total_rejected_icds?.status === '+',
              percentage: data.total_rejected_icds?.percentage != null
                ? `${data.total_rejected_icds.status === '-' ? '' : data.total_rejected_icds.status}${data.total_rejected_icds.percentage.toFixed(2)}`
                : null,
            },
            rejected_cpts: {
              value: data.total_rejected_cpts?.value,
              change: (data.total_rejected_cpts?.change ?? 0).toFixed(2),
              increase: data.total_rejected_cpts?.status === '+',
              percentage: data.total_rejected_cpts?.percentage != null
                ? `${data.total_rejected_cpts.status === '-' ? '' : data.total_rejected_cpts.status}${data.total_rejected_cpts.percentage.toFixed(2)}`
                : null,
            },
            rejected_drugs: {
              value: data.total_rejected_drugs?.value,
              change: (data.total_rejected_drugs?.change ?? 0).toFixed(2),
              increase: data.total_rejected_drugs?.status === '+',
              percentage: data.total_rejected_drugs?.percentage != null
                ? `${data.total_rejected_drugs.status === '-' ? '' : data.total_rejected_drugs.status}${data.total_rejected_drugs.percentage.toFixed(2)}`
                : null,
            },
            approved_ratio: {
              value: data.approved_ratio?.value,
              change: (data.approved_ratio?.change ?? 0).toFixed(2),
              increase: data.approved_ratio?.status === '+',
              percentage: data.approved_ratio?.percentage != null
                ? `${data.approved_ratio.status === '-' ? '' : data.approved_ratio.status}${data.approved_ratio.percentage.toFixed(2)}`
                : null,
            },
            total_patient_not_refile_yet: {
              value: data.total_patient_not_refile_yet?.value,
              change: (data.total_patient_not_refile_yet?.change ?? 0).toFixed(2),
              increase: data.total_patient_not_refile_yet?.status === '+',
              percentage: data.total_patient_not_refile_yet?.percentage != null
                ? `${data.total_patient_not_refile_yet.status === '-' ? '' : data.total_patient_not_refile_yet.status}${data.total_patient_not_refile_yet.percentage.toFixed(2)}`
                : null,
            },

            total_cost_on_upcoming_refiles: {
              value: data.total_cost_on_upcoming_refiles?.value,
              change: (data.total_cost_on_upcoming_refiles?.change ?? 0).toFixed(2),
              increase: data.total_cost_on_upcoming_refiles?.status === '+',
              percentage: data.total_cost_on_upcoming_refiles?.percentage != null
                ? `${data.total_cost_on_upcoming_refiles.status === '-' ? '' : data.total_cost_on_upcoming_refiles.status}${data.total_cost_on_upcoming_refiles.percentage.toFixed(2)}`
                : null,
            },

            total_patient_who_refile_this_month: {
              value: data.total_patient_who_refile_this_month?.value,
              change: (data.total_patient_who_refile_this_month?.change ?? 0).toFixed(2),
              increase: data.total_patient_who_refile_this_month?.status === '+',
              percentage: data.total_patient_who_refile_this_month?.percentage != null
                ? `${data.total_patient_who_refile_this_month.status === '-' ? '' : data.total_patient_who_refile_this_month.status}${data.total_patient_who_refile_this_month.percentage.toFixed(2)}`
                : null,
            },

            expensive_drugs_prescribed: {
              value: data.expensive_drugs_prescribed?.value,
              change: (data.expensive_drugs_prescribed?.change ?? 0).toFixed(2),
              increase: data.expensive_drugs_prescribed?.status === '+',
              percentage: data.expensive_drugs_prescribed?.percentage != null
                ? `${data.expensive_drugs_prescribed.status === '-' ? '' : data.expensive_drugs_prescribed.status}${data.expensive_drugs_prescribed.percentage.toFixed(2)}`
                : null,
            },

            patient_abusing_system: {
              value: data.patient_abusing_system?.value,
              change: (data.patient_abusing_system?.change ?? 0).toFixed(2),
              increase: data.patient_abusing_system?.status === '+',
              percentage: data.patient_abusing_system?.percentage != null
                ? `${data.patient_abusing_system.status === '-' ? '' : data.patient_abusing_system.status}${data.patient_abusing_system.percentage.toFixed(2)}`
                : null,
            },

            high_rejection_providers: {
              value: data.high_rejection_providers?.value,
              change: (data.high_rejection_providers?.change ?? 0).toFixed(2),
              increase: data.high_rejection_providers?.status === '+',
              percentage: data.high_rejection_providers?.percentage != null
                ? `${data.high_rejection_providers.status === '-' ? '' : data.high_rejection_providers.status}${data.high_rejection_providers.percentage.toFixed(2)}`
                : null,
            },

            excessive_encounters: {
              value: data.excessive_encounters?.value,
              change: (data.excessive_encounters?.change ?? 0).toFixed(2),
              increase: data.excessive_encounters?.status === '+',
              percentage: data.excessive_encounters?.percentage != null
                ? `${data.excessive_encounters.status === '-' ? '' : data.excessive_encounters.status}${data.excessive_encounters.percentage.toFixed(2)}`
                : null,
            },
          };
  
          setStatsData({...transformedData})
  
          setError(null);
      } catch (err) {
        if (!controller.signal.aborted) setError(err);
      } finally {
        if (!controller.signal.aborted && reqId === latestReqIdRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line
  }, [queryKey, type]); // IMPORTANT: not depending on `operation`

  return { statsData, loading, error };
};

export default useInsuranceAnalysisData;